import { useRef, useCallback, useEffect } from 'react'

/**
 * Enables click-and-drag horizontal scrolling on a scroll container,
 * mimicking a touch swipe on desktop — including flick momentum/inertia
 * so a fast swipe glides to a smooth stop. Also suppresses the click
 * that fires at the end of a drag so links/cards inside don't navigate
 * when the user was actually swiping.
 */
export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null)
  const state = useRef({
    isDown: false,
    pointerId: -1,
    startX: 0,
    scrollLeft: 0,
    moved: 0,
    lastX: 0,
    lastT: 0,
    velocity: 0,
  })
  const rafId = useRef<number | null>(null)

  const stopMomentum = useCallback(() => {
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current)
      rafId.current = null
    }
  }, [])

  const runMomentum = useCallback(() => {
    const el = ref.current
    if (!el) return
    const step = () => {
      const s = state.current
      s.velocity *= 0.94 // friction
      el.scrollLeft -= s.velocity
      if (Math.abs(s.velocity) > 0.4) {
        rafId.current = requestAnimationFrame(step)
      } else {
        rafId.current = null
      }
    }
    rafId.current = requestAnimationFrame(step)
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<T>) => {
      const el = ref.current
      if (!el) return
      if (e.pointerType === 'mouse' && e.button !== 0) return
      stopMomentum()
      // Capture the pointer so move/up events keep firing on this container
      // even as the cursor passes over child links/images.
      try {
        el.setPointerCapture(e.pointerId)
      } catch {
        /* no-op */
      }
      const s = state.current
      s.isDown = true
      s.pointerId = e.pointerId
      s.moved = 0
      s.startX = e.clientX
      s.scrollLeft = el.scrollLeft
      s.lastX = e.clientX
      s.lastT = performance.now()
      s.velocity = 0
    },
    [stopMomentum],
  )

  const onPointerMove = useCallback((e: React.PointerEvent<T>) => {
    const el = ref.current
    const s = state.current
    if (!el || !s.isDown) return
    const dx = e.clientX - s.startX
    s.moved = Math.max(s.moved, Math.abs(dx))
    el.scrollLeft = s.scrollLeft - dx

    // Track instantaneous velocity for the flick.
    const now = performance.now()
    const dt = now - s.lastT
    if (dt > 0) {
      s.velocity = ((e.clientX - s.lastX) / dt) * 16 // px per frame
    }
    s.lastX = e.clientX
    s.lastT = now
  }, [])

  const endDrag = useCallback(() => {
    const s = state.current
    if (!s.isDown) return
    s.isDown = false
    const el = ref.current
    if (el && s.pointerId !== -1) {
      try {
        el.releasePointerCapture(s.pointerId)
      } catch {
        /* no-op */
      }
    }
    s.pointerId = -1
    if (Math.abs(s.velocity) > 0.8) runMomentum()
  }, [runMomentum])

  // Swallow the click that follows a real drag so inner links don't fire.
  const onClickCapture = useCallback((e: React.MouseEvent<T>) => {
    if (state.current.moved > 6) {
      e.preventDefault()
      e.stopPropagation()
      state.current.moved = 0
    }
  }, [])

  useEffect(() => stopMomentum, [stopMomentum])

  // Prevent the browser's native image/link drag from hijacking the swipe.
  const onDragStart = useCallback((e: React.DragEvent<T>) => {
    e.preventDefault()
  }, [])

  return {
    ref,
    dragProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onClickCapture,
      onDragStart,
      style: { touchAction: 'pan-y' as const },
    },
  }
}
